import { addDays, isSunday, isValid, nextMonday, nextSunday, startOfToday } from 'date-fns';
import _ from 'lodash';
import React from 'react';
import { getDimColumnKey, useAppliedFilters } from './revenueUpliftDashboardComputations';
import { LOCATIONS_DIMENSION_ID } from './revenueUpliftConstants';
import { DashboardContext, DomainContext } from './revenueUpliftContexts';
import {
  DAY_DETAIL_METRICS,
  DAY_DETAIL_TIPS_METRICS,
  DAY_DETAIL_LABOR_METRICS,
  DAY_START_HOUR,
  DEFAULT_STAFFING_ROLE,
  HOURS_METRIC,
  INCOME_METRIC,
  INCOME_PER_EMPLOYEE_HOUR_METRIC,
  LABOR_COST_METRIC,
  LABOR_METRIC,
  LABOR_PERCENT_METRIC,
  MAX_SHIFT_LENGTH,
  MIN_HOURLY_REVENUE_PER_EMPLOYEE,
  MIN_HOURLY_STAFF,
  MIN_SHIFT_LENGTH,
  PERIOD_OPTIONS,
  SHIFTS_METRIC,
  STAFFING_RECOMMENDATIONS,
  STAFFING_ROLE_KEYS,
  TIPS_METRIC,
  TIPS_PER_EMPLOYEE_HOUR_METRIC,
  TIPS_PER_EMPLOYEE_METRIC,
  TOTAL_HEADCOUNT_METRIC,
  TOTAL_INCOME_METRIC,
  TOTAL_INCOME_PER_EMPLOYEE_METRIC,
  TOTAL_LABOR_COST_METRIC,
  TOTAL_TIPS_METRIC,
  WAGES_PLUS_TIPS_METRIC,
  WAGES_PLUS_TIPS_PER_EMPLOYEE_METRIC,
} from './staffingTabConstants';
import { formatAsDatasetDate, getDayOfWeek } from './timePeriod';
import { utcDate } from './relativeDates';
import { compactFormatNumber, formatNumber as simpleFormatNumber } from '../../../util/format';
import { HOUR_DIMENSION_TYPE } from '../../../workflow/workflowConstants';
import { useUpdateStaffingRoles } from '../../../../data-access/mutation/staffingRoles';
import {
  useStaffingRecommendations,
  useStaffingRecommendationsByLocations,
} from '../../../../data-access/query/recommendations';
import { useStaffingRoles } from '../../../../data-access/query/staffingRoles';
import { generateDatesBetween } from '../../../../utils/date-handling';

const areRolesDifferent = (role1, role2) => {
  return !_.isEqual(role1, role2);
};

const roleIsValid = role => {
  const allMetrics = [...DAY_DETAIL_METRICS, ...DAY_DETAIL_TIPS_METRICS, ...DAY_DETAIL_LABOR_METRICS];

  return (
    role[STAFFING_ROLE_KEYS.STAFFING_ROLE_NAME].length >= 1 &&
    role[STAFFING_ROLE_KEYS.MIN_HOURLY_STAFF_DAY] >= MIN_HOURLY_STAFF &&
    role[STAFFING_ROLE_KEYS.MIN_HOURLY_STAFF_CLOSE] >= MIN_HOURLY_STAFF &&
    role[STAFFING_ROLE_KEYS.MIN_HOURLY_REVENUE_PER_EMPLOYEE] >= 0 &&
    role[STAFFING_ROLE_KEYS.MAX_HOURLY_REVENUE_PER_EMPLOYEE] >=
      Math.max(role[STAFFING_ROLE_KEYS.MIN_HOURLY_REVENUE_PER_EMPLOYEE], MIN_HOURLY_REVENUE_PER_EMPLOYEE) &&
    role[STAFFING_ROLE_KEYS.MIN_HOURLY_TIPS_PER_EMPLOYEE] >= 0 &&
    role[STAFFING_ROLE_KEYS.MAX_HOURLY_TIPS_PER_EMPLOYEE] >= role[STAFFING_ROLE_KEYS.MIN_HOURLY_TIPS_PER_EMPLOYEE] &&
    role[STAFFING_ROLE_KEYS.MIN_SHIFT_LENGTH] >= MIN_SHIFT_LENGTH &&
    role[STAFFING_ROLE_KEYS.MIN_SHIFT_LENGTH] <= role[STAFFING_ROLE_KEYS.MAX_SHIFT_LENGTH] &&
    role[STAFFING_ROLE_KEYS.MAX_SHIFT_LENGTH] >= role[STAFFING_ROLE_KEYS.MIN_SHIFT_LENGTH] &&
    role[STAFFING_ROLE_KEYS.MAX_SHIFT_LENGTH] <= MAX_SHIFT_LENGTH &&
    role[STAFFING_ROLE_KEYS.AVERAGE_HOURLY_WAGES] >= 0 &&
    role[STAFFING_ROLE_KEYS.MIN_LABOR_COST_PCT] >= 0 &&
    role[STAFFING_ROLE_KEYS.MIN_LABOR_COST_PCT] <= role[STAFFING_ROLE_KEYS.MAX_LABOR_COST_PCT] &&
    role[STAFFING_ROLE_KEYS.MAX_LABOR_COST_PCT] >= role[STAFFING_ROLE_KEYS.MIN_LABOR_COST_PCT] &&
    Object.values(STAFFING_RECOMMENDATIONS).includes(role[STAFFING_ROLE_KEYS.BASE_RECOMMENDATIONS_ON]) &&
    allMetrics.includes(role[STAFFING_ROLE_KEYS.LAST_SELECTED_METRIC])
  );
};

export const isHalfHour = time => {
  const [, minutes] = time.split(':');
  return minutes === '30';
};

export const calculateShiftDuration = (start, end) => {
  const timeToMinutes = time => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const startMinutes = timeToMinutes(start);
  let endMinutes = timeToMinutes(end);

  // Handle overnight shifts (if end is before start, it means it passed midnight)
  if (endMinutes < startMinutes) {
    endMinutes += 1440; // Add 24 hours in minutes
  }

  const durationInMinutes = endMinutes - startMinutes;
  let durationInHours = durationInMinutes / 60;

  if (!isHalfHour(end)) {
    durationInHours += 1;
  }

  return durationInHours;
};

// Returns > 0 if hour1 is after hour2, < 0 if hour1 is before hour2, 0 if they are the same
export const compareHours = (hour1, hour2, hoursRange) => {
  return hoursRange.indexOf(hour1) - hoursRange.indexOf(hour2);
};

export const addHours = (hour, hoursToAdd, hoursRange) => {
  const hourIndex = hoursRange.indexOf(hour);

  return hoursRange[hourIndex + hoursToAdd] ?? hoursRange[hoursToAdd > 0 ? hoursRange.length - 1 : 0];
};

export const useExtendedHourRange = (normalHoursRange, shiftsPlan) => {
  // Convert "HH:MM" to total minutes since midnight
  const timeToMinutes = time => {
    const [hour, minute] = time.split(':').map(Number);
    return (hour * 60 + minute) % 1440; // Ensure values wrap correctly at 24:00
  };

  // Convert total minutes back to "HH:00"
  const minutesToTime = minutes => {
    const normalizedMinutes = (minutes + 1440) % 1440; // Handle wrap around midnight
    const hour = Math.floor(normalizedMinutes / 60);
    return `${String(hour).padStart(2, '0')}:00`;
  };

  // Compute the earliest start and latest end considering shifts
  const { finalStart, finalEnd } = React.useMemo(() => {
    if (!normalHoursRange.length) {
      return { finalStart: null, finalEnd: null };
    }

    const normalStart = timeToMinutes(normalHoursRange[0]);
    const normalEnd = timeToMinutes(normalHoursRange[normalHoursRange.length - 1]);

    let minTime = normalStart;
    let maxTime = normalEnd;

    shiftsPlan.forEach(({ start, end }) => {
      const startTime = timeToMinutes(start);
      const endTime = timeToMinutes(end);

      // Handle overnight shifts by ensuring end time is always after start
      const adjustedEndTime = endTime < startTime ? endTime + 1440 : endTime;

      minTime = Math.min(minTime, startTime);
      maxTime = Math.max(maxTime, adjustedEndTime);
    });

    // Extend the range by 1 hour only if necessary
    const extendedStart = minTime < normalStart ? Math.floor(minTime / 60) * 60 : normalStart - 60;
    const extendedEnd = maxTime > normalEnd ? Math.ceil(maxTime / 60) * 60 : normalEnd + 60;

    return { finalStart: minutesToTime(extendedStart), finalEnd: minutesToTime(extendedEnd) };
  }, [normalHoursRange, shiftsPlan]);

  // Generate the adjusted range (only full hours)
  const extendedHourRange = React.useMemo(() => {
    if (!finalStart || !finalEnd) {
      return normalHoursRange;
    }

    // If it's open 24hs
    if (normalHoursRange.length === 24) {
      return normalHoursRange;
    }

    const startMinutes = timeToMinutes(finalStart);
    const endMinutes = timeToMinutes(finalEnd);

    const totalSteps =
      endMinutes >= startMinutes ? (endMinutes - startMinutes) / 60 + 1 : (1440 - startMinutes + endMinutes) / 60 + 1;

    return Array.from({ length: totalSteps }, (__, i) => minutesToTime(startMinutes + i * 60));
  }, [finalStart, finalEnd, normalHoursRange]);

  return { extendedHourRange };
};

export const addHalfHoursToRange = hoursRange => {
  const extendedRange = [];
  hoursRange.forEach(hour => {
    extendedRange.push(hour); // Add full hour
    const [hh] = hour.split(':').map(Number);
    const nextHalfHour = `${hh.toString().padStart(2, '0')}:30`;
    extendedRange.push(nextHalfHour); // Add half hour
  });
  return extendedRange;
};

export const useStaffingRolesApi = ({ currentLocationId, locations, staffingEnabled }) => {
  const [staffingRoleChanges, setStaffingRoleChanges] = React.useState(() => {
    const initialRoles = [];

    locations.forEach(({ location_id: locationId }) => {
      initialRoles.push({
        location_id: locationId,
        staffing_roles: [DEFAULT_STAFFING_ROLE],
      });
    });

    return [initialRoles];
  });

  const { mutate: updateStaffingRoles, isLoading: isLoadingStaffingRolesUpdate } = useUpdateStaffingRoles();

  const { isLoading: isLoadingStaffingRoles } = useStaffingRoles({
    onSuccess: data => {
      const currentRoles = [...data];
      let additions = 0;

      locations.forEach(({ location_id: locationId }) => {
        const locationRoles = currentRoles.find(({ location_id: currentId }) => currentId === locationId);

        if (!locationRoles) {
          currentRoles.push({
            location_id: locationId,
            staffing_roles: [DEFAULT_STAFFING_ROLE],
          });
          additions += 1;
        } else if (!locationRoles.staffing_roles || locationRoles.staffing_roles.length === 0) {
          locationRoles.staffing_roles = [DEFAULT_STAFFING_ROLE];
          additions += 1;
        }
      });

      if (additions > 0) {
        // Update the staffing roles in the backend
        updateStaffingRoles(currentRoles);
      }

      // Overwrite the initial roles with the fetched ones
      const [, ...rest] = staffingRoleChanges;
      setStaffingRoleChanges([currentRoles, ...rest]);
    },
    enabled: staffingEnabled,
  });

  const getCurrentStaffingRoleFromChanges = React.useCallback((locationId, changes) => {
    const currentStaffingRolesChanges = changes[changes.length - 1];

    const currentLocationRoles =
      currentStaffingRolesChanges.find(({ location_id: currentId }) => currentId === locationId)?.staffing_roles ?? [];

    return currentLocationRoles.find(({ name }) => name === 'Default') ?? DEFAULT_STAFFING_ROLE;
  }, []);

  const [currentStaffingRole, setCurrentStaffingRole] = React.useState(() =>
    getCurrentStaffingRoleFromChanges(currentLocationId, staffingRoleChanges),
  );

  React.useEffect(() => {
    setCurrentStaffingRole(getCurrentStaffingRoleFromChanges(currentLocationId, staffingRoleChanges));
  }, [staffingRoleChanges, currentLocationId, getCurrentStaffingRoleFromChanges]);

  const saveCurrentStaffingRole = updatedRole => {
    const currentRole = updatedRole || currentStaffingRole;
    const previousRole = getCurrentStaffingRoleFromChanges(currentLocationId, staffingRoleChanges);
    const rolesDifferent = areRolesDifferent(previousRole, currentRole);
    const isRoleValid = roleIsValid(currentRole);

    if (rolesDifferent && isRoleValid) {
      const previousStaffingRoleChanges = staffingRoleChanges[staffingRoleChanges.length - 1];

      const currentStaffingRolesChanges = previousStaffingRoleChanges.map(locationStaffingRoles => {
        if (locationStaffingRoles.location_id === currentLocationId) {
          return {
            ...locationStaffingRoles,
            staffing_roles: locationStaffingRoles.staffing_roles.map(role => {
              if (role.name === currentRole.name) {
                return currentRole;
              }

              return role;
            }),
          };
        }

        return locationStaffingRoles;
      });

      updateStaffingRoles(currentStaffingRolesChanges);
      setStaffingRoleChanges(prev => [...prev, currentStaffingRolesChanges]);
    }
  };

  const undoLastChange = () => {
    const previousStaffingRoles = staffingRoleChanges[staffingRoleChanges.length - 1];

    if (previousStaffingRoles == null) {
      return;
    }

    setStaffingRoleChanges(staffingRoleChanges.slice(0, -1));
    updateStaffingRoles(previousStaffingRoles);
  };

  const canUndoLastChange = staffingRoleChanges.length > 1;

  return {
    isLoadingStaffingRoles,
    staffingRoleChanges,
    currentStaffingRole,
    setCurrentStaffingRole,
    saveCurrentStaffingRole,
    undoLastChange,
    canUndoLastChange,
    isLoadingStaffingRolesUpdate,
  };
};

const PERIOD_TO_LABEL = {
  [PERIOD_OPTIONS.NEXT_7_DAYS]: 'Next 7 Days',
  [PERIOD_OPTIONS.NEXT_14_DAYS]: 'Next 14 Days',
  [PERIOD_OPTIONS.NEXT_28_DAYS]: 'Next 28 Days',
  [PERIOD_OPTIONS.THIS_WEEK]: 'This Week',
  [PERIOD_OPTIONS.NEXT_WEEK]: 'Next Week',
};

export const usePeriodOptions = (getHoursOfOperationByDate, selectedLocation) => {
  const { minAndMaxDailyForecastDates } = React.useContext(DashboardContext);

  const getStartAndEndDates = React.useCallback(
    periodValue => {
      const [minDatasetDateString, maxDatasetDateString] = minAndMaxDailyForecastDates;
      const minDatasetDate = utcDate(minDatasetDateString);
      const maxDatasetDate = utcDate(maxDatasetDateString);
      const today = startOfToday();

      if (periodValue === PERIOD_OPTIONS.NEXT_7_DAYS) {
        const end = addDays(today, 6);

        return [_.max([today, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      if (periodValue === PERIOD_OPTIONS.NEXT_14_DAYS) {
        const end = addDays(today, 13);

        return [_.max([today, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      if (periodValue === PERIOD_OPTIONS.NEXT_28_DAYS) {
        const end = addDays(today, 27);

        return [_.max([today, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      if (periodValue === PERIOD_OPTIONS.THIS_WEEK) {
        const end = isSunday(today) ? today : nextSunday(today);

        return [_.max([today, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      if (periodValue === PERIOD_OPTIONS.NEXT_WEEK) {
        const start = nextMonday(today);
        const end = nextSunday(start);

        return [_.max([start, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      return [null, null];
    },
    [minAndMaxDailyForecastDates],
  );

  const periodOptions = React.useMemo(
    () =>
      Object.values(PERIOD_OPTIONS)
        .filter(p => {
          const [start, end] = getStartAndEndDates(p);

          return isValid(start) && isValid(end) && start <= end;
        })
        .map(option => ({
          label: PERIOD_TO_LABEL[option],
          value: option,
        })),
    [getStartAndEndDates],
  );

  const next28DaysOption = periodOptions.find(p => p.value === PERIOD_OPTIONS.NEXT_28_DAYS);

  const [selectedPeriod, setSelectedPeriod] = React.useState(next28DaysOption);
  const startAndEndDates = React.useMemo(() => getStartAndEndDates(selectedPeriod.value), [
    selectedPeriod.value,
    getStartAndEndDates,
  ]);
  const [selectedDate, setSelectedDate] = React.useState(startAndEndDates[0]);

  const dates = React.useMemo(() => {
    const [startDate, endDate] = startAndEndDates;
    if (!isValid(startDate) || !isValid(endDate)) {
      return [];
    }

    const startDateString = formatAsDatasetDate(startDate);
    const endDateString = formatAsDatasetDate(endDate);

    return generateDatesBetween(startDateString, endDateString).map(date => ({
      date,
      isClosed: getHoursOfOperationByDate(date).length === 0,
    }));
  }, [startAndEndDates, getHoursOfOperationByDate]);

  React.useEffect(() => {
    let newDate = selectedDate;
    const [start, end] = startAndEndDates;

    // if new date is out of range, set it to the start date of the period
    if (newDate < start) {
      newDate = start;
    }
    if (newDate > end) {
      newDate = start;
    }

    const dateStr = formatAsDatasetDate(newDate);
    const isClosed = dates.find(({ date }) => date === dateStr)?.isClosed ?? false;
    if (isClosed) {
      const openDay = dates.find(({ isClosed: closed }) => !closed)?.date;
      if (openDay) {
        newDate = utcDate(openDay);
      }
    }

    setSelectedDate(newDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAndEndDates, selectedLocation, dates, getHoursOfOperationByDate]);

  return {
    periodOptions,
    selectedPeriod,
    setSelectedPeriod,
    startAndEndDates,
    selectedDate,
    setSelectedDate,
    dates,
  };
};

export const usePeriodDates = period => {
  const { minAndMaxDailyForecastDates } = React.useContext(DashboardContext);

  const getStartAndEndDates = React.useCallback(
    periodValue => {
      const [minDatasetDateString, maxDatasetDateString] = minAndMaxDailyForecastDates;
      const minDatasetDate = utcDate(minDatasetDateString);
      const maxDatasetDate = utcDate(maxDatasetDateString);
      const today = startOfToday();

      if (periodValue === PERIOD_OPTIONS.NEXT_7_DAYS) {
        const end = addDays(today, 6);

        return [_.max([today, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      if (periodValue === PERIOD_OPTIONS.NEXT_14_DAYS) {
        const end = addDays(today, 13);

        return [_.max([today, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      if (periodValue === PERIOD_OPTIONS.NEXT_28_DAYS) {
        const end = addDays(today, 27);

        return [_.max([today, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      if (periodValue === PERIOD_OPTIONS.THIS_WEEK) {
        const end = isSunday(today) ? today : nextSunday(today);

        return [_.max([today, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      if (periodValue === PERIOD_OPTIONS.NEXT_WEEK) {
        const start = nextMonday(today);
        const end = nextSunday(start);

        return [_.max([start, minDatasetDate]), _.min([end, maxDatasetDate])];
      }

      return [null, null];
    },
    [minAndMaxDailyForecastDates],
  );

  const startAndEndDates = React.useMemo(() => getStartAndEndDates(period), [period, getStartAndEndDates]);

  const dates = React.useMemo(() => {
    const [startDate, endDate] = startAndEndDates;
    if (!isValid(startDate) || !isValid(endDate)) {
      return [];
    }

    const startDateString = formatAsDatasetDate(startDate);
    const endDateString = formatAsDatasetDate(endDate);

    return generateDatesBetween(startDateString, endDateString);
  }, [startAndEndDates]);

  return {
    startAndEndDates,
    dates,
  };
};

export const useLocationOptions = (selectionState, locations) => {
  const locationOptions = React.useMemo(() => {
    const locationsFilterState = selectionState[LOCATIONS_DIMENSION_ID];
    const locationsOptions = locations.map(l => ({
      value: l.location_id,
      label: l.location_description,
    }));

    const isUnselected = ({ isSelected }) => !isSelected;
    if (_.every(locationsFilterState, isUnselected)) {
      // If all locations are filtered out, show all of them in the dropdown
      return locationsOptions;
    }

    // Otherwise, only show locations in the dropdown that are not filtered out
    const selectedLocations = locationsOptions.filter(location => {
      const filterStateForLoc = locationsFilterState.find(
        ({ dimensionValueId }) => location.value === dimensionValueId,
      );

      return filterStateForLoc != null && filterStateForLoc.isSelected;
    });

    return _.sortBy(selectedLocations, 'label');
  }, [locations, selectionState]);

  const [selectedLocation, setSelectedLocation] = React.useState(locationOptions[0]);

  React.useEffect(() => {
    if (!locationOptions.some(({ value }) => value === selectedLocation.value)) {
      setSelectedLocation(locationOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationOptions]);

  return { locationOptions, selectedLocation, setSelectedLocation };
};

// Returns an array of all the hours in the day ordered starting from DAY_START_HOUR
const getHoursOrdered = () => {
  const hours = [];
  let currentHour = DAY_START_HOUR; // '04:00'

  for (let i = 0; i < 24; i++) {
    hours.push(currentHour);
    const [hour] = currentHour.split(':');
    const newHour = parseInt(hour, 10) + 1;
    if (newHour >= 24) {
      currentHour = '00:00';
    } else {
      currentHour = `${newHour.toString().padStart(2, '0')}:00`;
    }
  }

  return hours;
};

const useHoursOfOperationByLocationByDay = () => {
  const { shiftHoursPerDayPerLocation } = React.useContext(DashboardContext);

  const getHoursOfOperationByDate = React.useCallback(
    (dateStr, locationId) => {
      const locationHours = shiftHoursPerDayPerLocation[locationId];

      if (locationHours == null) {
        return getHoursOrdered();
      }

      const dayOfWeek = getDayOfWeek(utcDate(dateStr));
      const dayHours = locationHours[dayOfWeek];

      return dayHours?.map(({ hour }) => hour) ?? [];
    },
    [shiftHoursPerDayPerLocation],
  );

  return { getHoursOfOperationByDate };
};

export const useHoursOfOperationByDay = locationId => {
  const { shiftHoursPerDayPerLocation } = React.useContext(DashboardContext);

  const getHoursOfOperationByDate = React.useCallback(
    dateStr => {
      const locationHours = shiftHoursPerDayPerLocation[locationId];

      if (locationHours == null) {
        return getHoursOrdered();
      }

      const dayOfWeek = getDayOfWeek(utcDate(dateStr));
      const dayHours = locationHours[dayOfWeek];

      return dayHours?.map(({ hour }) => hour) ?? [];
    },
    [locationId, shiftHoursPerDayPerLocation],
  );

  const hasHoursOfOperationDefined = React.useMemo(() => {
    const locationHours = shiftHoursPerDayPerLocation[locationId];

    return locationHours != null;
  }, [locationId, shiftHoursPerDayPerLocation]);

  return { getHoursOfOperationByDate, hasHoursOfOperationDefined };
};

// For example return '2024-03-06' if the date is '2024-03-06' and the hour is between '04:00' and '23:00'
// or if the date is '2024-03-07' and the hour is between '00:00' and '03:00'
const getDayKey = (
  row,
  startDateString,
  endDateString,
  locationId,
  hourDimId,
  dimensionValues,
  locationHoursPerDay,
) => {
  const { tranDate, locationId: rowLocationId, [getDimColumnKey(hourDimId)]: hour } = row;

  if (rowLocationId !== locationId) {
    return null;
  }

  const hourValue = dimensionValues.find(dimValue => dimValue.dimension_id === hourDimId && dimValue.id === hour)
    ?.value;

  if (locationHoursPerDay == null) {
    // No hours of operation defined, use the default DAY_START_HOUR
    const dayBefore = addDays(utcDate(tranDate), -1);
    const dayBeforeString = formatAsDatasetDate(dayBefore);

    const dayKey = hourValue < DAY_START_HOUR ? dayBeforeString : tranDate;

    return dayKey >= startDateString && dayKey <= endDateString ? dayKey : null;
  }

  const currentDay = utcDate(tranDate);
  const dayOfWeek = getDayOfWeek(currentDay);
  const dayHours = locationHoursPerDay[dayOfWeek];

  if (dayHours.find(dayHour => dayHour.hour === hourValue && dayHour.isNextDay === false) != null) {
    return tranDate;
  }

  const dayBefore = addDays(currentDay, -1);
  const dayOfWeekBefore = getDayOfWeek(dayBefore);
  const dayBeforeHours = locationHoursPerDay[dayOfWeekBefore];

  if (dayBeforeHours.find(dayHour => dayHour.hour === hourValue && dayHour.isNextDay === true) != null) {
    return formatAsDatasetDate(dayBefore);
  }

  return null;
};

export const useRowsByDay = (startAndEndDates, locationId) => {
  const { dimensions, dimensionValues } = React.useContext(DomainContext);
  const { weeklyForecastStatsRows, weeklyForecastRows, shiftHoursPerDayPerLocation } = React.useContext(
    DashboardContext,
  );

  const [rowsByDay, rowsByDayAndHour] = React.useMemo(() => {
    const [start, end] = startAndEndDates;
    const startDateString = formatAsDatasetDate(start);
    const endDateString = formatAsDatasetDate(end);
    const hourDimId = dimensions.find(dim => dim.dimension_type === HOUR_DIMENSION_TYPE)?.product_dimension_id;
    const locationHoursPerDay = shiftHoursPerDayPerLocation[locationId];
    const rows = {};
    const hourRows = {};

    weeklyForecastRows.forEach(row => {
      const dayKey = getDayKey(
        row,
        startDateString,
        endDateString,
        locationId,
        hourDimId,
        dimensionValues,
        locationHoursPerDay,
      );

      const { [getDimColumnKey(hourDimId)]: hour } = row;
      const hourValue = dimensionValues.find(dimValue => dimValue.dimension_id === hourDimId && dimValue.id === hour)
        ?.value;

      if (dayKey != null) {
        if (!rows[dayKey]) {
          rows[dayKey] = [row];
        } else {
          rows[dayKey].push(row);
        }

        if (!hourRows[dayKey]) {
          hourRows[dayKey] = {
            [hourValue]: [row],
          };
        } else if (!hourRows[dayKey][hourValue]) {
          hourRows[dayKey][hourValue] = [row];
        } else {
          hourRows[dayKey][hourValue].push(row);
        }
      }
    });

    return [rows, hourRows];
  }, [startAndEndDates, locationId, weeklyForecastRows, dimensions, dimensionValues, shiftHoursPerDayPerLocation]);

  const gratuityRowsByDay = React.useMemo(() => {
    const [start, end] = startAndEndDates;
    const startDateString = formatAsDatasetDate(start);
    const endDateString = formatAsDatasetDate(end);
    const hourDimId = dimensions.find(dim => dim.dimension_type === HOUR_DIMENSION_TYPE)?.product_dimension_id;
    const locationHoursPerDay = shiftHoursPerDayPerLocation[locationId];
    const rows = {};

    weeklyForecastStatsRows.forEach(row => {
      const dayKey = getDayKey(
        row,
        startDateString,
        endDateString,
        locationId,
        hourDimId,
        dimensionValues,
        locationHoursPerDay,
      );

      if (dayKey != null && row.prediction >= 0.01) {
        if (!rows[dayKey]) {
          rows[dayKey] = [row];
        } else {
          rows[dayKey].push(row);
        }
      }
    });

    return rows;
  }, [startAndEndDates, locationId, weeklyForecastStatsRows, dimensions, dimensionValues, shiftHoursPerDayPerLocation]);

  return {
    rowsByDay,
    rowsByDayAndHour,
    gratuityRowsByDay,
  };
};

export const useRowsByDayMultipleLocations = () => {
  const { dimensions, dimensionValues } = React.useContext(DomainContext);
  const { weeklyForecastStatsRows, weeklyForecastRows, shiftHoursPerDayPerLocation, locationsApi } = React.useContext(
    DashboardContext,
  );
  const { locationOptions } = locationsApi;
  const { startAndEndDates } = usePeriodDates(PERIOD_OPTIONS.NEXT_28_DAYS);

  const rowsByLocationByDay = React.useMemo(() => {
    const [start, end] = startAndEndDates;
    const startDateString = formatAsDatasetDate(start);
    const endDateString = formatAsDatasetDate(end);
    const hourDimId = dimensions.find(dim => dim.dimension_type === HOUR_DIMENSION_TYPE)?.product_dimension_id;

    const allRows = {};

    locationOptions.forEach(location => {
      const locId = location.value;
      const locationHoursPerDay = shiftHoursPerDayPerLocation[locId];

      weeklyForecastRows.forEach(row => {
        const dayKey = getDayKey(
          row,
          startDateString,
          endDateString,
          locId,
          hourDimId,
          dimensionValues,
          locationHoursPerDay,
        );

        if (dayKey != null) {
          if (!allRows[locId]) {
            allRows[locId] = {};
          }
          if (!allRows[locId][dayKey]) {
            allRows[locId][dayKey] = [row];
          } else {
            allRows[locId][dayKey].push(row);
          }
        }
      });
    });

    return allRows;
  }, [startAndEndDates, dimensions, locationOptions, shiftHoursPerDayPerLocation, weeklyForecastRows, dimensionValues]);

  const gratuityRowsByLocationByDay = React.useMemo(() => {
    const [start, end] = startAndEndDates;
    const startDateString = formatAsDatasetDate(start);
    const endDateString = formatAsDatasetDate(end);
    const hourDimId = dimensions.find(dim => dim.dimension_type === HOUR_DIMENSION_TYPE)?.product_dimension_id;

    const allGratuityRows = {};

    locationOptions.forEach(location => {
      const locId = location.value;
      const locationHoursPerDay = shiftHoursPerDayPerLocation[locId];

      weeklyForecastStatsRows.forEach(row => {
        const dayKey = getDayKey(
          row,
          startDateString,
          endDateString,
          locId,
          hourDimId,
          dimensionValues,
          locationHoursPerDay,
        );

        if (dayKey != null && row.prediction >= 0.01) {
          if (!allGratuityRows[locId]) {
            allGratuityRows[locId] = {};
          }
          if (!allGratuityRows[locId][dayKey]) {
            allGratuityRows[locId][dayKey] = [row];
          } else {
            allGratuityRows[locId][dayKey].push(row);
          }
        }
      });
    });

    return allGratuityRows;
  }, [
    startAndEndDates,
    dimensions,
    locationOptions,
    shiftHoursPerDayPerLocation,
    weeklyForecastStatsRows,
    dimensionValues,
  ]);

  return {
    rowsByLocationByDay,
    gratuityRowsByLocationByDay,
  };
};

const calculateLaborCost = (shifts, averageHourlyWages) => {
  const laborCostByHour = {};
  let laborCostTotal = 0;

  shifts.forEach(shift => {
    const startHour = parseInt(shift.start.split(':')[0], 10);
    let endHour = parseInt(shift.end.split(':')[0], 10);

    // Handle overnight shifts (if endHour is before startHour, it passed midnight)
    if (endHour < startHour) {
      endHour += 24;
    }

    if (startHour === endHour) {
      // Handle shifts that start and end in the same hour
      if (isHalfHour(shift.start) || isHalfHour(shift.end)) {
        laborCostByHour[startHour % 24] = (laborCostByHour[startHour % 24] || 0) + 0.5;
      } else {
        laborCostByHour[startHour % 24] = (laborCostByHour[startHour % 24] || 0) + 1;
      }
    } else {
      // Add the first hour
      if (isHalfHour(shift.start)) {
        laborCostByHour[startHour % 24] = (laborCostByHour[startHour % 24] || 0) + 0.5;
      } else {
        laborCostByHour[startHour % 24] = (laborCostByHour[startHour % 24] || 0) + 1;
      }

      // Add intermediate hours
      for (let hour = startHour + 1; hour < endHour; hour++) {
        laborCostByHour[hour % 24] = (laborCostByHour[hour % 24] || 0) + 1;
      }

      // Add the last hour
      if (isHalfHour(shift.end)) {
        laborCostByHour[endHour % 24] = (laborCostByHour[endHour % 24] || 0) + 0.5;
      } else {
        laborCostByHour[endHour % 24] = (laborCostByHour[endHour % 24] || 0) + 1;
      }
    }
  });

  Object.keys(laborCostByHour).forEach(hour => {
    // Calculate the total labor cost per hour
    laborCostTotal += laborCostByHour[hour] * averageHourlyWages;
  });

  return { laborCostTotal, laborCostByHour };
};

export const useStatistics = (
  rowsByDay,
  gratuityRowsByDay,
  shiftsRecommendations,
  shiftPlansByDay,
  getHoursOfOperationByDate,
  dates,
) => {
  const { isPriceOptimizationProfit } = React.useContext(DomainContext);
  const { staffingRolesApi, staffingPlannerApi, locationsApi } = React.useContext(DashboardContext);
  const { selectedLocation } = locationsApi;
  const { value: locationId } = selectedLocation;
  const { currentStaffingRole } = staffingRolesApi;
  const { staffingPlanner = {} } = staffingPlannerApi;

  const { [STAFFING_ROLE_KEYS.AVERAGE_HOURLY_WAGES]: averageHourlyWages } = currentStaffingRole;

  const dayStatistics = React.useMemo(() => {
    const incomeMetricKey = isPriceOptimizationProfit ? 'currentProfit' : 'currentRevenue';
    const metrics = {};
    const planMetrics = {};

    dates.forEach(({ date }) => {
      const rows = rowsByDay[date] || [];
      const gratuityRows = gratuityRowsByDay[date] || [];
      const dayShifts = shiftsRecommendations.find(rec => rec.date === date)?.shifts ?? [];
      const planShiftsByDay = shiftPlansByDay[date] ?? [];
      const staffingPlannerShifts = staffingPlanner[locationId]?.[date];
      // use staffingPlanner saved shifts if available, otherwise use the shifts from the forecast
      const planDayShifts = staffingPlannerShifts || planShiftsByDay;
      const dayHoursOfOperation = getHoursOfOperationByDate(date);

      const dayIncome = _.sumBy(rows, incomeMetricKey);
      const dayTips = _.sumBy(gratuityRows, 'prediction') || 0;
      const employeeHours = dayShifts.reduce(
        (acc, { start, end }) => acc + compareHours(end, start, dayHoursOfOperation) + 1,
        0,
      );
      const planEmployeeHours = planDayShifts.reduce(
        (acc, { start, end }) => acc + calculateShiftDuration(start, end),
        0,
      );

      const tipsPerEmployee = employeeHours > 0 ? dayTips / employeeHours : undefined;
      const planTipsPerEmployee = planEmployeeHours > 0 ? dayTips / planEmployeeHours : undefined;

      const { laborCostTotal: dayLaborCost } = calculateLaborCost(dayShifts, averageHourlyWages);
      const { laborCostTotal: planLaborCost } = calculateLaborCost(planDayShifts, averageHourlyWages);

      metrics[date] = {
        [INCOME_METRIC]: dayIncome,
        [TIPS_METRIC]: dayTips,
        [SHIFTS_METRIC]: dayShifts.length,
        [HOURS_METRIC]: employeeHours,
        [INCOME_PER_EMPLOYEE_HOUR_METRIC]: employeeHours > 0 ? dayIncome / employeeHours : undefined,
        [TIPS_PER_EMPLOYEE_HOUR_METRIC]: tipsPerEmployee,
        [LABOR_METRIC]: dayIncome > 0 ? (dayLaborCost / dayIncome) * 100 : 0,
        [LABOR_COST_METRIC]: dayLaborCost,
        [WAGES_PLUS_TIPS_METRIC]: averageHourlyWages + tipsPerEmployee,
      };
      planMetrics[date] = {
        [SHIFTS_METRIC]: planDayShifts.length,
        [HOURS_METRIC]: planEmployeeHours,
        [INCOME_PER_EMPLOYEE_HOUR_METRIC]: planEmployeeHours > 0 ? dayIncome / planEmployeeHours : undefined,
        [TIPS_PER_EMPLOYEE_HOUR_METRIC]: planTipsPerEmployee,
        [LABOR_METRIC]: dayIncome > 0 ? (planLaborCost / dayIncome) * 100 : 0,
        [LABOR_COST_METRIC]: planLaborCost,
        [WAGES_PLUS_TIPS_METRIC]: averageHourlyWages + planTipsPerEmployee,
      };
    });

    return { statisticsByDay: metrics, planStatisticsByDay: planMetrics };
  }, [
    isPriceOptimizationProfit,
    dates,
    rowsByDay,
    gratuityRowsByDay,
    shiftsRecommendations,
    shiftPlansByDay,
    staffingPlanner,
    locationId,
    getHoursOfOperationByDate,
    averageHourlyWages,
  ]);

  const periodTotalStatistics = React.useMemo(() => {
    const { statisticsByDay, planStatisticsByDay } = dayStatistics;

    const totalIncome = _.sumBy(Object.values(statisticsByDay), INCOME_METRIC);
    const totalTips = _.sumBy(Object.values(statisticsByDay), TIPS_METRIC);
    const totalShifts = _.sumBy(Object.values(statisticsByDay), SHIFTS_METRIC);
    const totalHours = _.sumBy(Object.values(statisticsByDay), HOURS_METRIC);
    const totalLaborCost = _.sumBy(Object.values(statisticsByDay), LABOR_COST_METRIC);
    const totalIncomePerEmployeeHour = totalHours > 0 ? totalIncome / totalHours : undefined;
    const totalTipsPerEmployeeHour = totalHours > 0 ? totalTips / totalHours : undefined;
    const totalLaborPercent = totalIncome > 0 ? (totalLaborCost / totalIncome) * 100 : 0;
    const totalWagesPlusTipsPerEmployee = averageHourlyWages + totalTipsPerEmployeeHour;

    const planTotalShifts = _.sumBy(Object.values(planStatisticsByDay), SHIFTS_METRIC);
    const planTotalHours = _.sumBy(Object.values(planStatisticsByDay), HOURS_METRIC);
    const planTotalLaborCost = _.sumBy(Object.values(planStatisticsByDay), LABOR_COST_METRIC);
    const planTotalIncomePerEmployeeHour = planTotalHours > 0 ? totalIncome / planTotalHours : undefined;
    const planTotalTipsPerEmployeeHour = planTotalHours > 0 ? totalTips / planTotalHours : undefined;
    const planTotalLaborPercent = totalIncome > 0 ? (planTotalLaborCost / totalIncome) * 100 : 0;
    const planTotalWagesPlusTipsPerEmployee = averageHourlyWages + planTotalTipsPerEmployeeHour;

    return {
      periodStatistics: {
        [INCOME_METRIC]: totalIncome,
        [TIPS_METRIC]: totalTips,
        [SHIFTS_METRIC]: totalShifts,
        [HOURS_METRIC]: totalHours,
        [INCOME_PER_EMPLOYEE_HOUR_METRIC]: totalIncomePerEmployeeHour,
        [TIPS_PER_EMPLOYEE_HOUR_METRIC]: totalTipsPerEmployeeHour,
        [LABOR_METRIC]: totalLaborPercent,
        [LABOR_COST_METRIC]: totalLaborCost,
        [WAGES_PLUS_TIPS_METRIC]: totalWagesPlusTipsPerEmployee,
      },
      planPeriodStatistics: {
        [INCOME_METRIC]: totalIncome,
        [TIPS_METRIC]: totalTips,
        [SHIFTS_METRIC]: planTotalShifts,
        [HOURS_METRIC]: planTotalHours,
        [INCOME_PER_EMPLOYEE_HOUR_METRIC]: planTotalIncomePerEmployeeHour,
        [TIPS_PER_EMPLOYEE_HOUR_METRIC]: planTotalTipsPerEmployeeHour,
        [LABOR_METRIC]: planTotalLaborPercent,
        [LABOR_COST_METRIC]: planTotalLaborCost,
        [WAGES_PLUS_TIPS_METRIC]: planTotalWagesPlusTipsPerEmployee,
      },
    };
  }, [averageHourlyWages, dayStatistics]);

  return {
    ...periodTotalStatistics,
    ...dayStatistics,
  };
};

export const useLocationDayStatistics = (
  rowsByLocationByDay,
  gratuityRowsByLocationByDay,
  shiftsRecommendationsByLocation,
) => {
  const { isPriceOptimizationProfit } = React.useContext(DomainContext);
  const { locationsApi } = React.useContext(DashboardContext);
  const { locationOptions } = locationsApi;

  const { getHoursOfOperationByDate } = useHoursOfOperationByLocationByDay();
  const { dates } = usePeriodDates(PERIOD_OPTIONS.NEXT_28_DAYS);

  const dayStatisticsList = React.useMemo(() => {
    const incomeMetricKey = isPriceOptimizationProfit ? 'currentProfit' : 'currentRevenue';
    const statisticsList = [];

    locationOptions.forEach(location => {
      const locId = location.value;

      dates.forEach(date => {
        const rows = rowsByLocationByDay[locId]?.[date] || [];
        const gratuityRows = gratuityRowsByLocationByDay[locId]?.[date] || [];
        const dayShifts = shiftsRecommendationsByLocation[locId]?.find(rec => rec.date === date)?.shifts || [];
        const dayHoursOfOperation = getHoursOfOperationByDate(date, locId);

        const dayIncome = _.sumBy(rows, incomeMetricKey);
        const dayTips = _.sumBy(gratuityRows, 'prediction') || 0;
        const employeeHours = dayShifts.reduce(
          (acc, { start, end }) => acc + compareHours(end, start, dayHoursOfOperation) + 1,
          0,
        );

        statisticsList.push({
          locationId: locId,
          date,
          isClosed: dayHoursOfOperation.length === 0,
          [INCOME_METRIC]: dayIncome,
          [TIPS_METRIC]: dayTips,
          [SHIFTS_METRIC]: dayShifts.length,
          [HOURS_METRIC]: employeeHours,
          [INCOME_PER_EMPLOYEE_HOUR_METRIC]: employeeHours > 0 ? dayIncome / employeeHours : undefined,
          [TIPS_PER_EMPLOYEE_HOUR_METRIC]: employeeHours > 0 ? dayTips / employeeHours : undefined,
        });
      });
    });

    return statisticsList;
  }, [
    isPriceOptimizationProfit,
    locationOptions,
    dates,
    rowsByLocationByDay,
    gratuityRowsByLocationByDay,
    shiftsRecommendationsByLocation,
    getHoursOfOperationByDate,
  ]);

  return dayStatisticsList;
};

export const useHourlyStatistics = (rows, gratuityRows, dayShifts, hoursRange) => {
  const { isPriceOptimizationProfit, dimensions, dimensionValues } = React.useContext(DomainContext);
  const { staffingRolesApi } = React.useContext(DashboardContext);
  const { currentStaffingRole } = staffingRolesApi;

  return React.useMemo(() => {
    const hourDimId = dimensions.find(dim => dim.dimension_type === HOUR_DIMENSION_TYPE)?.product_dimension_id;
    const incomeMetricKey = isPriceOptimizationProfit ? 'currentProfit' : 'currentRevenue';
    const { [STAFFING_ROLE_KEYS.AVERAGE_HOURLY_WAGES]: averageHourlyWages } = currentStaffingRole;
    const recommendedPlan = {
      [TOTAL_INCOME_METRIC]: {},
      [TOTAL_TIPS_METRIC]: {},
      [TOTAL_INCOME_PER_EMPLOYEE_METRIC]: {},
      [TIPS_PER_EMPLOYEE_METRIC]: {},
      [TOTAL_HEADCOUNT_METRIC]: {},
      [LABOR_PERCENT_METRIC]: {},
      [TOTAL_LABOR_COST_METRIC]: {},
      [WAGES_PLUS_TIPS_PER_EMPLOYEE_METRIC]: {},
    };

    rows.forEach(row => {
      const hour = row[getDimColumnKey(hourDimId)];
      const hourValue = dimensionValues.find(dimValue => dimValue.dimension_id === hourDimId && dimValue.id === hour)
        ?.value;

      const previous = recommendedPlan[TOTAL_INCOME_METRIC][hourValue] ?? 0;
      recommendedPlan[TOTAL_INCOME_METRIC][hourValue] = previous + row[incomeMetricKey];
    });

    gratuityRows.forEach(row => {
      const hour = row[getDimColumnKey(hourDimId)];
      const hourValue = dimensionValues.find(dimValue => dimValue.dimension_id === hourDimId && dimValue.id === hour)
        ?.value;

      const previous = recommendedPlan[TOTAL_TIPS_METRIC][hourValue] ?? 0;
      recommendedPlan[TOTAL_TIPS_METRIC][hourValue] = previous + row.prediction;
    });

    hoursRange.forEach(hour => {
      const headcount = dayShifts.filter(
        ({ start, end }) => compareHours(hour, start, hoursRange) >= 0 && compareHours(end, hour, hoursRange) >= 0,
      ).length;

      const hourIncome = recommendedPlan[TOTAL_INCOME_METRIC][hour] ?? 0;
      const hourTips = recommendedPlan[TOTAL_TIPS_METRIC][hour] ?? 0;

      const tipsPerEmployee = headcount > 0 ? hourTips / headcount : 0;

      recommendedPlan[TOTAL_HEADCOUNT_METRIC][hour] = headcount;
      recommendedPlan[TOTAL_INCOME_PER_EMPLOYEE_METRIC][hour] = headcount > 0 ? hourIncome / headcount : 0;
      recommendedPlan[TIPS_PER_EMPLOYEE_METRIC][hour] = tipsPerEmployee;
      recommendedPlan[LABOR_PERCENT_METRIC][hour] =
        hourIncome > 0 ? ((headcount * averageHourlyWages) / hourIncome) * 100 : 0;
      recommendedPlan[TOTAL_LABOR_COST_METRIC][hour] = headcount * averageHourlyWages;
      recommendedPlan[WAGES_PLUS_TIPS_PER_EMPLOYEE_METRIC][hour] = averageHourlyWages + tipsPerEmployee;
    });

    return recommendedPlan;
  }, [
    dimensions,
    isPriceOptimizationProfit,
    currentStaffingRole,
    rows,
    gratuityRows,
    hoursRange,
    dimensionValues,
    dayShifts,
  ]);
};

export const usePlannerHourlyStatistics = (forecast, dayShifts, hoursRange) => {
  const { staffingRolesApi } = React.useContext(DashboardContext);
  const { currentStaffingRole } = staffingRolesApi;
  const { [STAFFING_ROLE_KEYS.AVERAGE_HOURLY_WAGES]: averageHourlyWages } = currentStaffingRole;

  const { extendedHourRange } = useExtendedHourRange(hoursRange, dayShifts);
  const halfHoursExtendedRange = addHalfHoursToRange(extendedHourRange);

  return React.useMemo(() => {
    const plan = {
      [TOTAL_INCOME_PER_EMPLOYEE_METRIC]: {},
      [TIPS_PER_EMPLOYEE_METRIC]: {},
      [TOTAL_HEADCOUNT_METRIC]: {},
      [LABOR_PERCENT_METRIC]: {},
      [TOTAL_LABOR_COST_METRIC]: {},
      [WAGES_PLUS_TIPS_PER_EMPLOYEE_METRIC]: {},
    };

    extendedHourRange.forEach(hour => {
      const [currentHour] = hour.split(':');

      const headcount = dayShifts.filter(({ start, end }) => {
        // Includes shifts that start within the next 30 minutes
        const isBefore = compareHours(hour, start, halfHoursExtendedRange) >= -1; // Adjust to include the previous half hour
        const isAfter = compareHours(end, hour, halfHoursExtendedRange) >= 0;

        return isBefore && isAfter;
      }).length;

      // Calculate the headcount for half-hour shifts
      const halfHourHeadcount = dayShifts.reduce((count, { start, end }) => {
        const isBefore = compareHours(hour, start, halfHoursExtendedRange) >= -1;
        const isAfter = compareHours(end, hour, halfHoursExtendedRange) >= 0;

        if (isBefore && isAfter) {
          const [currentStartHour] = start.split(':');
          const [currentEndHour] = end.split(':');

          const startPartial = isHalfHour(start) && currentHour === currentStartHour ? 0.5 : 1;
          const endPartial = isHalfHour(end) && currentHour === currentEndHour ? 0.5 : 1;
          return count + Math.min(startPartial, endPartial);
        }

        return count;
      }, 0);

      const hourIncome = forecast[TOTAL_INCOME_METRIC][hour] ?? 0;
      const hourTips = forecast[TOTAL_TIPS_METRIC][hour] ?? 0;

      const tipsPerEmployee = halfHourHeadcount > 0 ? hourTips / halfHourHeadcount : 0;
      const wagesPlusTipsPerEmployee = halfHourHeadcount > 0 ? averageHourlyWages + tipsPerEmployee : 0;

      plan[TOTAL_HEADCOUNT_METRIC][hour] = headcount;
      plan[TOTAL_INCOME_PER_EMPLOYEE_METRIC][hour] = halfHourHeadcount > 0 ? hourIncome / halfHourHeadcount : 0;
      plan[TIPS_PER_EMPLOYEE_METRIC][hour] = tipsPerEmployee;
      plan[LABOR_PERCENT_METRIC][hour] =
        hourIncome > 0 ? ((halfHourHeadcount * averageHourlyWages) / hourIncome) * 100 : 0;
      plan[TOTAL_LABOR_COST_METRIC][hour] = halfHourHeadcount * averageHourlyWages;
      plan[WAGES_PLUS_TIPS_PER_EMPLOYEE_METRIC][hour] = wagesPlusTipsPerEmployee;
    });

    return plan;
  }, [extendedHourRange, dayShifts, forecast, averageHourlyWages, halfHoursExtendedRange]);
};

// Returns a slice of hoursOrdered that includes all the hours between the first and last hours
// that have forecasted revenue or gratuity for the given day
const getDayHoursRange = (rowsByHour, dayGratuityRows, hourDimId, dimensionValues, hoursOrdered) => {
  const gratuityHours = dayGratuityRows.map(row => {
    const hour = row[getDimColumnKey(hourDimId)];
    const hourValue = dimensionValues.find(dimValue => dimValue.dimension_id === hourDimId && dimValue.id === hour);
    return hourValue?.value;
  });

  const minRevHour = _.minBy(Object.keys(rowsByHour), hour => hoursOrdered.indexOf(hour));
  const minGratuityHour = _.minBy(gratuityHours, hour => hoursOrdered.indexOf(hour));
  const minHours = [];
  if (minRevHour != null) {
    minHours.push(minRevHour);
  }
  if (minGratuityHour != null) {
    minHours.push(minGratuityHour);
  }
  const minHour = _.minBy(minHours, hour => hoursOrdered.indexOf(hour)) ?? hoursOrdered[0];

  const maxRevHour = _.maxBy(Object.keys(rowsByHour), hour => hoursOrdered.indexOf(hour));
  const maxGratuityHour = _.maxBy(gratuityHours, hour => hoursOrdered.indexOf(hour));
  const maxHours = [];
  if (maxRevHour != null) {
    maxHours.push(maxRevHour);
  }
  if (maxGratuityHour != null) {
    maxHours.push(maxGratuityHour);
  }
  const maxHour = _.maxBy(maxHours, hour => hoursOrdered.indexOf(hour)) ?? hoursOrdered[hoursOrdered.length - 1];

  return hoursOrdered.slice(hoursOrdered.indexOf(minHour), hoursOrdered.indexOf(maxHour) + 1);
};

// A day's hour range can be incomplete, e.g. ['09:00', '10:00', '11:00'],
// in which case it returns ['09:00', '10:00', '11:00', '12:00', ... '08:00']
export const getAllDayHoursFromHourRange = hoursRange => {
  const allHours = getHoursOrdered();

  const startHourIndex = allHours.indexOf(hoursRange[0]);
  const endHourIndex = allHours.indexOf(hoursRange[hoursRange.length - 1]);

  return [...hoursRange, ...allHours.slice(endHourIndex + 1), ...allHours.slice(0, startHourIndex)];
};

// e.g. if the shift ends at '18:00' then the last shift hour is '17:00'
export const getLastShiftHour = (shiftEndHour, hoursRange) => {
  const allHours = getAllDayHoursFromHourRange(hoursRange);
  const extendedRange = addHalfHoursToRange(allHours);
  return extendedRange[extendedRange.indexOf(shiftEndHour) - 2] ?? extendedRange[extendedRange.length - 2];
};

// e.g. if the last working hour of the shift is '17:00' then '18:00' is the hour the shift ends
export const getShiftEndTime = (lastWorkingHour, hoursRange) => {
  if (isHalfHour(lastWorkingHour)) {
    return lastWorkingHour;
  }

  const allHours = getAllDayHoursFromHourRange(hoursRange);
  const extendedRange = addHalfHoursToRange(allHours);
  return extendedRange[extendedRange.indexOf(lastWorkingHour) + 2] ?? extendedRange[0];
};

export const getShiftDuration = (start, end, hoursRange) => compareHours(end, start, hoursRange) + 1;

export const useOpenHours = (
  rowsByDayAndHour,
  gratuityRowsByDay,
  getHoursOfOperationByDate,
  hasHoursOfOperationDefined,
) => {
  const { dimensions, dimensionValues } = React.useContext(DomainContext);

  return React.useMemo(() => {
    const hourDimId = dimensions.find(dim => dim.dimension_type === HOUR_DIMENSION_TYPE)?.product_dimension_id;

    const hoursByDay = {};

    Object.entries(rowsByDayAndHour).forEach(([date, rowsByHour]) => {
      const dayGratuityRows = gratuityRowsByDay[date] ?? [];

      let hoursRange;
      if (hasHoursOfOperationDefined) {
        hoursRange = getHoursOfOperationByDate(date);
      } else {
        const hoursOrdered = getHoursOrdered();
        hoursRange = getDayHoursRange(rowsByHour, dayGratuityRows, hourDimId, dimensionValues, hoursOrdered);
      }

      hoursByDay[date] = {
        hoursRange,
      };
    });

    return hoursByDay;
  }, [
    rowsByDayAndHour,
    gratuityRowsByDay,
    dimensions,
    dimensionValues,
    getHoursOfOperationByDate,
    hasHoursOfOperationDefined,
  ]);
};

export const useShiftPlans = (selectedDate, shiftsRecommendations, dayShifts, hoursRange, hourlyStatistics) => {
  const { staffingRolesApi, staffingPlannerApi, locationsApi } = React.useContext(DashboardContext);
  const { currentStaffingRole } = staffingRolesApi;
  const { selectedLocation } = locationsApi;
  const { value: locationId } = selectedLocation;
  const { staffingPlanner, addShift, addShifts, editShift, deleteShift, deleteDay } = staffingPlannerApi;

  const [shiftPlansByDay, setShiftPlansByDay] = React.useState(() => {
    return shiftsRecommendations.reduce((map, { date, shifts }) => {
      map[date] = shifts;
      return map;
    }, {});
  });
  const editsByDay = React.useRef({});

  React.useEffect(() => {
    setShiftPlansByDay(prev => {
      const newMap = {};
      shiftsRecommendations.forEach(({ date, shifts }) => {
        newMap[date] = editsByDay.current[date] ? prev[date] || shifts : shifts;
      });
      const sameKeys =
        Object.keys(prev).length === Object.keys(newMap).length && Object.keys(prev).every(k => prev[k] === newMap[k]);

      return sameKeys ? prev : newMap;
    });
  }, [shiftsRecommendations]);

  const staffingPlannerShifts = React.useMemo(
    () => staffingPlanner?.[locationId]?.[formatAsDatasetDate(selectedDate)] ?? [],
    [staffingPlanner, locationId, selectedDate],
  );
  const staffingPlannerDefined = React.useMemo(
    () => staffingPlanner?.[locationId]?.[formatAsDatasetDate(selectedDate)] != null,
    [staffingPlanner, locationId, selectedDate],
  );
  const dayShiftPlans = React.useMemo(() => {
    const shiftPlans = shiftPlansByDay[formatAsDatasetDate(selectedDate)] ?? [];
    // If there are saved shifts in the staffing planner, use those instead of the RoverRecs
    return staffingPlannerShifts.length > 0 || staffingPlannerDefined ? staffingPlannerShifts : shiftPlans;
  }, [shiftPlansByDay, selectedDate, staffingPlannerShifts, staffingPlannerDefined]);

  const addDayShift = React.useCallback(() => {
    const { [STAFFING_ROLE_KEYS.MIN_SHIFT_LENGTH]: minShiftLength } = currentStaffingRole;

    const dateKey = formatAsDatasetDate(selectedDate);
    const newShift = {
      start: hoursRange[0],
      end: addHours(hoursRange[0], minShiftLength - 1, hoursRange),
      shift: dayShiftPlans.length + 1,
    };

    if (staffingPlannerShifts.length === 0) {
      // save previous shifts and new shift to dashboard settings
      addShifts(locationId, dateKey, [...dayShiftPlans, newShift]);
    } else {
      // save shift to dashboard settings
      addShift(locationId, dateKey, newShift);
    }

    setShiftPlansByDay(prevShiftPlansByDay => {
      return {
        ...prevShiftPlansByDay,
        [dateKey]: [...dayShiftPlans, newShift],
      };
    });

    editsByDay.current = {
      ...editsByDay.current,
      [dateKey]: true,
    };
  }, [
    currentStaffingRole,
    selectedDate,
    hoursRange,
    dayShiftPlans,
    staffingPlannerShifts,
    addShifts,
    locationId,
    addShift,
  ]);

  const removeDayShift = React.useCallback(
    shiftNumber => {
      const dateKey = formatAsDatasetDate(selectedDate);

      if (staffingPlannerShifts.length === 0) {
        // save shifts to dashboard settings without the deleted shift
        const newShifts = dayShiftPlans
          .filter(({ shift }) => shift !== shiftNumber)
          .map((shift, index) => ({
            ...shift,
            shift: index + 1,
          }));
        addShifts(locationId, dateKey, newShifts);
      } else {
        // delete shift from dashboard settings
        const shiftIndex = staffingPlannerShifts.findIndex(({ shift }) => shift === shiftNumber);
        if (shiftIndex !== -1) {
          deleteShift(locationId, dateKey, shiftIndex);
        }
      }

      setShiftPlansByDay(prevShiftPlansByDay => {
        return {
          ...prevShiftPlansByDay,
          [dateKey]: dayShiftPlans
            .filter(({ shift }) => shift !== shiftNumber)
            .map((shift, i) => {
              return {
                ...shift,
                shift: i + 1,
              };
            }),
        };
      });

      editsByDay.current = {
        ...editsByDay.current,
        [dateKey]: true,
      };
    },
    [addShifts, dayShiftPlans, deleteShift, locationId, selectedDate, staffingPlannerShifts],
  );

  const resetDayShifts = React.useCallback(() => {
    const dateKey = formatAsDatasetDate(selectedDate);

    // delete the day shifts from dashboard settings
    deleteDay(locationId, dateKey);

    setShiftPlansByDay(prevShiftPlansByDay => {
      return {
        ...prevShiftPlansByDay,
        [dateKey]: dayShifts,
      };
    });

    editsByDay.current = {
      ...editsByDay.current,
      [dateKey]: false,
    };
  }, [dayShifts, deleteDay, locationId, selectedDate]);

  const setDayShiftPlans = React.useCallback(
    (newShiftPlans, editedShift) => {
      const dateKey = formatAsDatasetDate(selectedDate);

      if (staffingPlannerShifts.length === 0) {
        // save shifts to dashboard settings
        addShifts(locationId, dateKey, newShiftPlans);
      } else {
        // edit shift
        const newShift = newShiftPlans.find(({ shift }) => shift === editedShift);
        const shiftIndex = staffingPlannerShifts.findIndex(({ shift }) => shift === editedShift);
        editShift(locationId, dateKey, shiftIndex, newShift);
      }

      setShiftPlansByDay(prevShiftPlansByDay => {
        return {
          ...prevShiftPlansByDay,
          [dateKey]: newShiftPlans,
        };
      });

      editsByDay.current = {
        ...editsByDay.current,
        [dateKey]: true,
      };
    },
    [addShifts, editShift, locationId, selectedDate, staffingPlannerShifts],
  );

  const hourlyPlanStatistics = usePlannerHourlyStatistics(hourlyStatistics, dayShiftPlans, hoursRange);

  return {
    shiftPlansByDay,
    dayShiftPlans,
    setDayShiftPlans,
    addDayShift,
    removeDayShift,
    resetDayShifts,
    hourlyPlanStatistics,
  };
};

export const formatNumber = (value, { currencySymbol = null, showCents = false } = {}) => {
  if (value == null) {
    return '-';
  }

  if (value < 10000) {
    return `${currencySymbol ?? ''}${simpleFormatNumber(Math.round(value))}`;
  }

  const fixedDecimalDigits = value < 100 ? 2 : 1;

  if (currencySymbol != null) {
    return compactFormatNumber(value, { formatAsCurrency: true, currencySymbol, fixedDecimalDigits, showCents });
  }

  return compactFormatNumber(value, { formatAsCurrency: false, fixedDecimalDigits, showCents });
};

export const useShiftsRecommendations = startAndEndDates => {
  const { tipForecastWorkflow, workflowId } = React.useContext(DomainContext);
  const { locationsApi, staffingRolesApi } = React.useContext(DashboardContext);
  const { filtersCount } = useAppliedFilters();

  const { currentStaffingRole } = staffingRolesApi;
  const { selectedLocation } = locationsApi;

  const [startDate, endDate] = React.useMemo(() => {
    const [start, end] = startAndEndDates;
    return [formatAsDatasetDate(start), formatAsDatasetDate(end)];
  }, [startAndEndDates]);

  const config = React.useMemo(() => {
    return {
      min_hourly_staff_day: currentStaffingRole[STAFFING_ROLE_KEYS.MIN_HOURLY_STAFF_DAY],
      min_hourly_staff_close: currentStaffingRole[STAFFING_ROLE_KEYS.MIN_HOURLY_STAFF_CLOSE],
      max_hourly_revenue_per_employee: currentStaffingRole[STAFFING_ROLE_KEYS.MAX_HOURLY_REVENUE_PER_EMPLOYEE],
      min_shift_length: currentStaffingRole[STAFFING_ROLE_KEYS.MIN_SHIFT_LENGTH],
      max_shift_length: currentStaffingRole[STAFFING_ROLE_KEYS.MAX_SHIFT_LENGTH],
      min_labor_cost_pct: currentStaffingRole[STAFFING_ROLE_KEYS.MIN_LABOR_COST_PCT],
      base_recommendations_on: currentStaffingRole[STAFFING_ROLE_KEYS.BASE_RECOMMENDATIONS_ON],
      staffing_role: currentStaffingRole[STAFFING_ROLE_KEYS.STAFFING_ROLE_NAME],
      average_hourly_wages: currentStaffingRole[STAFFING_ROLE_KEYS.AVERAGE_HOURLY_WAGES],
      workflow_id: workflowId,
      tip_forecast_workflow_id: tipForecastWorkflow?.workflow_id,
      location_id: selectedLocation.value,
      start_date: startDate,
      end_date: endDate,
      apply_filters: filtersCount > 0,
    };
  }, [
    currentStaffingRole,
    endDate,
    filtersCount,
    selectedLocation.value,
    startDate,
    tipForecastWorkflow?.workflow_id,
    workflowId,
  ]);

  const { data: shiftsRecommendations = [], isFetching: isLoadingShiftsRecommendations } = useStaffingRecommendations(
    config,
    filtersCount,
  );

  return {
    shiftsRecommendations,
    isLoadingShiftsRecommendations,
  };
};

export const useShiftsRecommendationsByLocation = startAndEndDates => {
  const { tipForecastWorkflow, workflowId, locations } = React.useContext(DomainContext);
  const { staffingRolesApi } = React.useContext(DashboardContext);
  const { currentStaffingRole } = staffingRolesApi;
  const { filtersCount } = useAppliedFilters();

  const locationIds = React.useMemo(() => locations.map(location => location.location_id), [locations]);

  const [startDate, endDate] = React.useMemo(() => {
    const [start, end] = startAndEndDates;
    return [formatAsDatasetDate(start), formatAsDatasetDate(end)];
  }, [startAndEndDates]);

  const baseConfig = React.useMemo(() => {
    return {
      min_hourly_staff_day: currentStaffingRole[STAFFING_ROLE_KEYS.MIN_HOURLY_STAFF_DAY],
      min_hourly_staff_close: currentStaffingRole[STAFFING_ROLE_KEYS.MIN_HOURLY_STAFF_CLOSE],
      max_hourly_revenue_per_employee: currentStaffingRole[STAFFING_ROLE_KEYS.MAX_HOURLY_REVENUE_PER_EMPLOYEE],
      min_shift_length: currentStaffingRole[STAFFING_ROLE_KEYS.MIN_SHIFT_LENGTH],
      max_shift_length: currentStaffingRole[STAFFING_ROLE_KEYS.MAX_SHIFT_LENGTH],
      min_labor_cost_pct: currentStaffingRole[STAFFING_ROLE_KEYS.MIN_LABOR_COST_PCT],
      average_hourly_wages: currentStaffingRole[STAFFING_ROLE_KEYS.AVERAGE_HOURLY_WAGES],
      base_recommendations_on: currentStaffingRole[STAFFING_ROLE_KEYS.BASE_RECOMMENDATIONS_ON],
      workflow_id: workflowId,
      tip_forecast_workflow_id: tipForecastWorkflow?.workflow_id,
      start_date: startDate,
      end_date: endDate,
      apply_filters: filtersCount > 0,
      staffing_role: currentStaffingRole[STAFFING_ROLE_KEYS.STAFFING_ROLE_NAME],
    };
  }, [currentStaffingRole, endDate, filtersCount, startDate, tipForecastWorkflow?.workflow_id, workflowId]);

  const {
    dataByLocation: shiftsRecommendationsByLocation,
    isFetching: isLoadingShiftsRecommendations,
  } = useStaffingRecommendationsByLocations(baseConfig, locationIds);

  return {
    shiftsRecommendationsByLocation,
    isLoadingShiftsRecommendations,
  };
};
